/**
 * @file: BottomPanel.tsx
 *
 * Shared in-place overlay panel — a floating sheet on mobile, a centered
 * card on desktop. This is the chrome behind the chat "AI 回复" composer
 * and the inbox contact picker; both render their own header / body /
 * footer as `children`.
 *
 * NOTE: distinct from the gorhom `BottomSheet` used elsewhere (different
 * UX). Hosted in a transparent RN `Modal` (so it overlays the full screen
 * wherever it's mounted) + `KeyboardAvoidingView` (so a child input clears
 * the keyboard), animated with moti `AnimatePresence` + `MotiView`. The
 * Modal stays mounted until the exit animation completes (`onExitComplete`).
 * Tapping the dimmer or the Android back button calls `onClose`.
 */

import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { AnimatePresence, MotiView } from 'moti';
import { FC, ReactNode, memo, useEffect, useState } from 'react';
import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export interface BottomPanelProps {
  visible: boolean;
  onClose: () => void;
  children: ReactNode;
  /**
   * Size the mobile sheet to its content (capped) instead of the default
   * ~60% viewport height. For short choosers (a few rows + a button) where
   * the fixed height would leave a large empty gap.
   */
  fitContent?: boolean;
  /**
   * Nearly the full height on mobile, for a detail that reads as a page
   * (an import's summary) while staying over the conversation.
   */
  tall?: boolean;
  /** The close animation finished and the modal is gone. */
  onExitComplete?: () => void;
  /** The centered card's width on desktop; a phone's sheet ignores it. */
  width?: number;
}

export const BottomPanel: FC<BottomPanelProps> = memo(
  ({
    visible,
    onClose,
    children,
    fitContent = false,
    tall = false,
    onExitComplete,
    width,
  }) => {
    const isMobile = useIsMobileLayout();
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();

    // Keep the Modal mounted while the exit animation plays.
    const [present, setPresent] = useState(visible);
    useEffect(() => {
      if (visible) setPresent(true);
    }, [visible]);

    if (!present) return null;

    return (
      <Modal transparent visible animationType="none" onRequestClose={onClose}>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={{ flex: 1 }}
        >
          <AnimatePresence
            onExitComplete={() => {
              setPresent(false);
              onExitComplete?.();
            }}
          >
            {visible ? (
              <MotiView
                key="overlay"
                from={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ type: 'timing', duration: 200 }}
                style={{
                  flex: 1,
                  backgroundColor: 'rgba(0,0,0,0.35)',
                  justifyContent: isMobile ? 'flex-end' : 'center',
                  alignItems: isMobile ? 'stretch' : 'center',
                }}
              >
                <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
                <MotiView
                  key="panel"
                  from={{
                    opacity: 0,
                    translateY: isMobile ? rpx(60) : 0,
                    scale: isMobile ? 1 : 0.96,
                  }}
                  animate={{ opacity: 1, translateY: 0, scale: 1 }}
                  exit={{
                    opacity: 0,
                    translateY: isMobile ? rpx(60) : 0,
                    scale: isMobile ? 1 : 0.96,
                  }}
                  transition={{ type: 'timing', duration: 220 }}
                  style={{
                    backgroundColor: '#fff',
                    // Floating sheet on mobile (inset from the edges, gap
                    // above the home indicator); centered card on desktop.
                    width: isMobile ? undefined : width ?? rpx(480),
                    marginHorizontal: isMobile ? rpx(12) : 0,
                    marginBottom: isMobile ? insets.bottom + rpx(8) : 0,
                    // ~60% of the viewport on mobile (or content height when
                    // `fitContent`); capped card on desktop.
                    height: isMobile
                      ? fitContent
                        ? undefined
                        : tall
                        ? '88%'
                        : '60%'
                      : undefined,
                    maxHeight: isMobile
                      ? fitContent
                        ? '80%'
                        : undefined
                      : tall
                      ? '86%'
                      : '74%',
                    borderRadius: rpx(36),
                    overflow: 'hidden',
                  }}
                >
                  {/* When `fitContent` the panel has no fixed height, so the
                      content host must size to its children (a `flex: 1` here
                      has `flex-basis: 0` and collapses to zero on device). With
                      a fixed-height sheet, `flex: 1` fills it as before. */}
                  {/* `flexShrink` (basis: content) lets a fitted sheet shrink
                      to its max height so a scroll view inside can scroll. */}
                  <View style={fitContent ? { flexShrink: 1 } : { flex: 1 }}>
                    {children}
                  </View>
                </MotiView>
              </MotiView>
            ) : null}
          </AnimatePresence>
        </KeyboardAvoidingView>
      </Modal>
    );
  },
);
